import type {Seat, SeatPlan, SurveyTask} from "./types"

export default function MeasurementSketch({
    task,
    seatPlan,
}: {
    task: SurveyTask
    seatPlan: SeatPlan
}) {
    const width = 640
    const height = 280
    const margin = {left: 46, right: 28, top: 55, bottom: 30}
    const seats = seatPlan.seats
    const xs = seats.map(seat => seat.position.x)
    const ys = seats.map(seat => seat.position.y)
    const minX = Math.min(...xs, 0)
    const maxX = Math.max(...xs, 1)
    const minY = Math.min(...ys, 0)
    const maxY = Math.max(...ys, 1)
    const plotWidth = width - margin.left - margin.right
    const plotHeight = height - margin.top - margin.bottom
    const point = (seat: Seat) => ({
        x: margin.left + (seat.position.x - minX) /
            Math.max(maxX - minX, .001) * plotWidth,
        y: margin.top + (seat.position.y - minY) /
            Math.max(maxY - minY, .001) * plotHeight,
    })
    const target = seats.find(seat => seat.key === task.targetSeatKey)
    let endSeat = task.referenceSeatKey
        ? seats.find(seat => seat.key === task.referenceSeatKey)
        : undefined
    if (target && task.type === "SEAT_PITCH") {
        const row = seats.filter(seat =>
            seat.section === target.section && seat.row === target.row
        ).sort((a, b) => a.column - b.column)
        const index = row.findIndex(seat => seat.key === target.key)
        endSeat = row[Math.min(row.length - 1,
            index + Number(task.spanCount ?? 1))]
    }
    if (target && task.type === "ROW_PITCH") {
        const targetRow = target.row + Number(task.spanCount ?? 1)
        endSeat = seats.filter(seat =>
            seat.section === target.section && seat.row > target.row
        ).sort((a, b) =>
            Math.abs(a.row - targetRow) - Math.abs(b.row - targetRow) ||
            Math.abs(a.column - target.column) -
            Math.abs(b.column - target.column))[0]
    }
    const targetPoint = target ? point(target) : undefined
    const endPoint = endSeat ? point(endSeat) : undefined
    const roomDimension = task.type === "ROOM_DIMENSION"

    return <div className="mb-3">
        <div className="small fw-semibold mb-1">
            Messskizze <span className="text-muted fw-normal">
                (schematisch, nicht maßstabsgetreu)
            </span>
        </div>
        <div className="border rounded bg-body-tertiary p-2">
            <svg viewBox={`0 0 ${width} ${height}`} role="img"
                aria-label={`Messskizze für ${task.title}`}
                style={{display: "block", width: "100%", maxHeight: 320}}>
                <defs><marker id={`arrow-${task.key}`} markerWidth="8"
                    markerHeight="8" refX="6" refY="4" orient="auto">
                    <path d="M0,0 L8,4 L0,8 z" fill="currentColor"/>
                </marker></defs>
                <rect x={margin.left} y={margin.top} width={plotWidth}
                    height={plotHeight} rx="4" fill="none"
                    stroke="currentColor" strokeWidth="2" opacity=".65"/>
                <rect x={width / 2 - 90} y="13" width="180" height="20"
                    rx="3" fill="#0d6efd" opacity=".85"/>
                <text x={width / 2} y="27" textAnchor="middle"
                    fill="white" fontSize="12" fontWeight="600">
                    PROJEKTIONSFLÄCHE · VORNE
                </text>
                {roomDimension ? <>
                    <MeasurementLine x1={margin.left + 12} y1={margin.top + 22}
                        x2={width - margin.right - 12} y2={margin.top + 22}
                        marker={task.key} color="#0d6efd"/>
                    <MeasurementLine x1={margin.left + 22} y1={margin.top + 52}
                        x2={margin.left + 22} y2={height - margin.bottom - 12}
                        marker={task.key} color="#198754"/>
                </> : <>
                    {seats.map(seat => {
                        const position = point(seat)
                        const highlighted = seat.key === target?.key ||
                            seat.key === endSeat?.key
                        return <circle key={seat.key} cx={position.x}
                            cy={position.y} r={highlighted ? 7 : 2.7}
                            fill={highlighted ? "#dc3545" : "#6c757d"}/>
                    })}
                    {targetPoint && endPoint &&
                        <MeasurementLine x1={targetPoint.x} y1={targetPoint.y}
                            x2={endPoint.x} y2={endPoint.y}
                            marker={task.key} color="#dc3545"/>}
                </>}
            </svg>
        </div>
    </div>
}

const MeasurementLine = ({x1, y1, x2, y2, marker, color}: {
    x1: number
    y1: number
    x2: number
    y2: number
    marker: string
    color: string
}) => <line x1={x1} y1={y1} x2={x2} y2={y2}
    stroke={color} strokeWidth="3" markerEnd={`url(#arrow-${marker})`}/>
